import Vue from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { ChatMessage, ConversationRoles } from '@src/lib/mistral-api'
import { AiChatBot, AIToolChoice } from '@src/lib/ai-client'
import { ExerciseService } from '@src/service/exercise'
import { ExerciseResponse } from '@src/data/exercise-response'
import { availableEquipment, eventSystem, NO_EQUIPMENT } from '@src/util/constants'
import { getCachedSystemPrompt, getSystemPrompt, getSystemPromptFingerprint, setCachedSystemPrompt } from '@src/components/ai-chat-widget/system-prompt'
import { defaultExerciseFilters } from '@src/state/dafaults'
import Club from '@src/data/club'
import { WorkoutExercise } from '@src/data/workout-exercise'
import WorkoutData from '@src/data/workout-data'
import { CreateElement } from 'vue'
import { planWorkout, WorkoutPlannerMode } from '@src/components/ai-chat-widget/workout-planner'
import {
  buildExerciseFacetCounts,
  compactExerciseFacetCounts,
  ExerciseFacetCounts
} from '@src/util/exercise-facet-counts'

const AI_LIBRARY_TAG_TYPES = [
  'ExerciseTags::Execution',
  'ExerciseTags::Category',
  'ExerciseTags::BodyArea',
  'ExerciseTags::MuscleGroup',
  'ExerciseTags::Joints',
  'ExerciseTags::MovementPattern',
  'ExerciseTags::RangeOfMotion',
  'ExerciseTags::Exercise',
  'ExerciseTags::Focus',
  'ExerciseTags::ClinicalPictures'
]
const MAX_AMBIGUOUS_EXERCISE_NAMES = 5
const UNTITLED_WORKOUT_NAME = 'untitled workout'

interface QuestionData {
  question: string
  options: string[]
  requiredRangeOfMotion?: boolean
  rangeOfMotionSignature?: string
  slot?: WorkoutRequestSlot
}

interface RangeOfMotionAmbiguity {
  signature: string
  exerciseNames: string[]
  options: string[]
}

type WorkoutRequestSlot = 'duration' | 'category' | 'execution' | 'target' | 'equipment' | 'rangeOfMotion'

interface WorkoutRequestSlotState {
  value: any
  noPreference?: boolean
}

interface WorkoutRequestState {
  duration: WorkoutRequestSlotState
  category: WorkoutRequestSlotState
  execution: WorkoutRequestSlotState
  target: WorkoutRequestSlotState
  equipment: WorkoutRequestSlotState
  rangeOfMotion: WorkoutRequestSlotState
}

const REQUIRED_WORKOUT_SLOTS: WorkoutRequestSlot[] = ['duration', 'category', 'execution', 'target']
const DEFAULT_CATEGORIES = ['fitness', 'rehab', 'kids']
const DEFAULT_EXECUTIONS = ['standing', 'sitting', 'lying']
const DEFAULT_TARGETS = [
  'abs',
  'back',
  'legs',
  'biceps',
  'calves',
  'chest',
  'triceps',
  'pelvic_floor',
  'glutes',
  'shoulder',
  'knees',
  'knee',
  'hip',
  'c_spine',
  'elbow',
  'wrist',
  'ankles',
  'finger',
  'herniated_disc',
  'knee_tep',
  'shoulder_tep',
  'hip_tep',
  'cervical_spine_c_spine',
  'lumbar_spine_lws',
  'thoracic_spine_tsp'
]

@Component({
  computed: {
    availableExercises: {
      get: function () {
        const state = this.$store.state || {}
        const list = Array.isArray(state.availableExercises) ? state.availableExercises : []
        const filters = state.exerciseFilters || {}
        const name = typeof filters.name === 'string' ? filters.name : ''
        const tags = filters.tags || new Set([])
        const aiTags = tags instanceof Set ? tags : { ...tags, phase: new Set([]) }
        const favoritesOnly = filters.favoritesOnly || false

        let filteredExercises = list.filter((exercise) =>
          ExerciseService.filterByTagsAndEquipment(exercise, aiTags) &&
          (ExerciseService.filterByName(exercise, name) || ExerciseService.filterByNumber(exercise, name)) &&
          ExerciseService.filterByFavorites(exercise, favoritesOnly, state.favoriteExerciseIds)
        )
        // sort
        if (!name || !name.trim().length) {
          return filteredExercises
        } else if (isNaN(Number(name))) {
          return filteredExercises.sort((a, b) => a.getName() > b.getName() ? 1 : a.getName() < b.getName() ? -1 : 0)
        } else {
          return filteredExercises.sort((a, b) => a.number > b.number ? 1 : a.number < b.number ? -1 : 0)
        }
      }
    },
    selectedWorkoutExercises () {
      return this.$store.state.new_workout.workoutExercises
    },
    exerciseTags () {
      return this.$store.getters.exerciseTags
    },
    club () {
      return this.$store.state.club
    },
    workout () {
      return this.$store.state.new_workout
    }
  }
})
export default class AIChatLogic extends Vue {
  @Prop({ required: false, default: 'ai-chat-widget' })
  filterFunction: Function

  @Prop({ required: false })
  removeFilterFunction: Function

  @Prop({ required: false, default: 'default' })
  availableTags: any

  @Prop({ required: false })
  loadingCallbacks: any

  @Prop({ required: false })
  closeChat: Function

  // Conversation State
  private messages: ChatMessage[] = []
  private initialConversation: ChatMessage[] = []
  private questionResponseResolver: ((response: string) => void) | null = null
  private currentQuestion: QuestionData | null = null
  private followUpQuestionCount: number = 0
  private allowPlainTextResponse: boolean = false
  private allowedRangeOfMotionAmbiguitySignature: string | null = null
  private hasNoRangeOfMotionPreference: boolean = false
  private workoutRequestState: WorkoutRequestState = this.createEmptyWorkoutRequestState()

  // Exercise & Workout State
  private availableExercises: ExerciseResponse[]
  private exerciseTags: any
  private club: Club
  private workout: WorkoutData
  private selectedWorkoutExercises: WorkoutExercise[]

  // AI Configuration
  private tools: any
  private toolHandlers: any
  private combinedSystemPrompt: string = ''
  private systemPromptFingerprint: string = ''
  private maxNumberOfAddedExercises: number = 40
  private maxToolLoops: number = 12
  private lastRequestHistory: ChatMessage[] | null = null
  private conversationTurnNumber: number = 0
  private activeRequestId: number = 0
  private currentToolRequestId: number = 0
  private lastBuildStatus: any = null

  // Token Management
  private tokenThreshold: number = 300000
  private maxUserMessages: number = 10
  private totalTokensUsed: number = 0
  private conversationStopped: boolean = false

  // Model Switching
  private exercisesAddedToWorkout: boolean = false
  private exercisesAddedInCurrentResponse: boolean = false

  // Event Handling
  private filterEventHandler: (e: CustomEvent) => void

  // User Preferences
  private userRequestedDurationMinutes: number = 15
  private activeHealthLimitations: Set<string> = new Set<string>()

  mounted () {
    this.initializeAILogic()
  }

  render (h: CreateElement) {
    return null
  }

  beforeDestroy () {
    this.cleanupAILogic()
  }

  // Public methods for UI component to use
  getMessages (): ChatMessage[] {
    return this.messages
  }

  getCurrentQuestion (): QuestionData | null {
    return this.currentQuestion
  }

  isConversationStopped (): boolean {
    return this.conversationStopped
  }

  hasReachedUserMessageLimit (): boolean {
    return this.getUserMessageCount() >= this.maxUserMessages
  }

  getUserMessageCount (): number {
    return this.messages.filter(message => message.role === ConversationRoles.USER).length
  }

  getMaxUserMessages (): number {
    return this.maxUserMessages
  }

  public getReportPayload (userDescription: string): { reportText: string, conversationFingerprint: string } {
    const conversation = this.messages
      .filter(message => !!message && message.role !== ConversationRoles.SYSTEM)
      .map((message, index) => this.formatReportMessage(message, index + 1))
      .filter(line => !!line)
      .join('\n\n')
    const selectedExercises = this.getSelectedExerciseNames()
    const reportText = [
      'PixFit AI Conversation Report',
      `Reported at: ${new Date().toISOString()}`,
      '',
      'User report:',
      userDescription.trim(),
      '',
      'Selected exercises:',
      selectedExercises.length ? selectedExercises.map(name => `- ${name}`).join('\n') : '- None',
      '',
      'Conversation and tool use:',
      conversation || 'No conversation messages were recorded.'
    ].join('\n')

    return {
      reportText,
      conversationFingerprint: this.createReportFingerprint(conversation, selectedExercises)
    }
  }

  public async retryLastRequest (): Promise<string> {
    if (this.conversationStopped) return ''

    const requestId = this.startRequest()
    const history = this.lastRequestHistory ? this.lastRequestHistory.slice() : this.cleanConversationHistory()
    try {
      return await this.processAIConversation(history, false, requestId)
    } catch (error) {
      if (this.isRequestActive(requestId)) this.handleAIResponseError(error)
      return ''
    } finally {
      if (this.isRequestCurrent(requestId)) this.logConversationTurn()
    }
  }

  @Watch('availableExercises', { immediate: true })
  onAvailableExercisesChange (newExercises: ExerciseResponse[]) {
    const count = Array.isArray(newExercises) ? newExercises.length : 0
    eventSystem.dispatchEvent(new CustomEvent('filteredExercises', {
      detail: { message: count.toString(), time: Date.now() }
    }))
  }
  addUserMessage (content: string): void {
    if (!this.canAcceptUserMessage()) return
    this.messages.push({ role: ConversationRoles.USER, content })
    this.updateWorkoutRequestStateFromText(content)
  }

  restartConversation (clearExercises: boolean = true): void {
    this.invalidateActiveRequest()
    this.cancelPendingQuestion()
    this.messages = this.initialConversation.slice()
    this.currentQuestion = null
    this.questionResponseResolver = null
    this.totalTokensUsed = 0
    this.conversationStopped = false
    this.exercisesAddedToWorkout = false
    this.exercisesAddedInCurrentResponse = false
    this.followUpQuestionCount = 0
    this.allowPlainTextResponse = false
    this.allowedRangeOfMotionAmbiguitySignature = null
    this.hasNoRangeOfMotionPreference = false
    this.activeHealthLimitations = new Set<string>()
    this.workoutRequestState = this.createEmptyWorkoutRequestState()
    this.conversationTurnNumber = 0
    this.lastBuildStatus = null
    if (clearExercises) {
      this.$store.commit('removeExercisesFromWorkout')
      this.$store.state.new_workout.name = ''
    }
    this.$store.dispatch('filterExercises', defaultExerciseFilters())
  }

  selectOption (option: string): void {
    if (!this.canAcceptUserMessage()) return

    if (this.questionResponseResolver) {
      this.answerCurrentQuestion(option)
    } else {
      this.messages.push({ role: ConversationRoles.USER, content: option })
      this.fetchAIResponse(false).then(r => (r))
    }
  }

  sendMessage (input: string): void {
    if (!input || this.conversationStopped) return
    if (!this.canAcceptUserMessage()) return

    if (this.questionResponseResolver) {
      this.answerCurrentQuestion(input)
    } else {
      this.currentQuestion = null
      this.messages.push({ role: ConversationRoles.USER, content: input })
      this.updateWorkoutRequestStateFromText(input)

      // Set intelligent loading message based on user input
      const loadingMessage = this.getLoadingMessageFromInput(input)
      if (this.loadingCallbacks && this.loadingCallbacks.setLoadingMessage) {
        this.loadingCallbacks.setLoadingMessage(loadingMessage)
      }

      this.fetchAIResponse(false).then(r => (r))
    }
  }

  public getSystemPrompt (): string {
    return this.combinedSystemPrompt
  }

  public getDefaultSystemPrompt (): string {
    return this.buildDefaultSystemPrompt()
  }

  public updateSystemPrompt (prompt: string, persist: boolean = true): void {
    this.combinedSystemPrompt = prompt
    if (persist) setCachedSystemPrompt(prompt, this.systemPromptFingerprint)
    this.replaceSystemPromptInMessages(prompt)
  }

  private answerCurrentQuestion (answer: string): boolean {
    const question = this.currentQuestion
    const resolver = this.questionResponseResolver
    if (!resolver) return false

    if (question && question.requiredRangeOfMotion) {
      return this.answerRangeOfMotionQuestion(question, answer, resolver)
    }

    this.messages.push({ role: ConversationRoles.USER, content: answer, uiOnly: true })
    this.updateWorkoutRequestStateFromQuestionAnswer(question, answer)
    this.currentQuestion = null
    this.questionResponseResolver = null
    resolver(answer)
    return true
  }

  private answerRangeOfMotionQuestion (question: QuestionData, answer: string, resolver: (response: string) => void): boolean {
    const matchedOption = this.findMatchingQuestionOption(question, answer)
    if (!matchedOption) {
      this.messages.push({ role: ConversationRoles.USER, content: answer, uiOnly: true })
      this.messages.push({
        role: ConversationRoles.ASSISTANT,
        content: 'Please choose one of the shown range of motion options.',
        uiOnly: true
      } as ChatMessage)
      this.$emit('conversation-updated')
      return false
    }

    const anyOption = `${this.$t('AI_ASSISTANT_ANY_OPTION')}`
    const selectedAny = matchedOption.trim().toLocaleLowerCase() === anyOption.trim().toLocaleLowerCase()
    this.messages.push({ role: ConversationRoles.USER, content: matchedOption, uiOnly: true })
    this.updateWorkoutRequestStateFromQuestionAnswer(question, matchedOption)
    this.hasNoRangeOfMotionPreference = selectedAny
    this.allowedRangeOfMotionAmbiguitySignature = selectedAny
      ? null
      : question.rangeOfMotionSignature || null
    if (!selectedAny) {
      this.filterFunction(matchedOption, 'range')
    }

    this.currentQuestion = null
    this.questionResponseResolver = null
    resolver(JSON.stringify({
      selected_range_of_motion: matchedOption,
      filter_applied: !selectedAny
    }))
    return true
  }

  private findMatchingQuestionOption (question: QuestionData, answer: string): string | null {
    const normalizedAnswer = (answer || '').trim().toLocaleLowerCase()
    if (!normalizedAnswer) return null
    return question.options.find(option => option.trim().toLocaleLowerCase() === normalizedAnswer) || null
  }

  private cancelPendingQuestion (): void {
    if (!this.questionResponseResolver) {
      this.currentQuestion = null
      return
    }

    const resolver = this.questionResponseResolver
    this.currentQuestion = null
    this.questionResponseResolver = null
    resolver(JSON.stringify({ success: false, cancelled: true }))
  }

  private canAcceptUserMessage (): boolean {
    if (!this.hasReachedUserMessageLimit()) return true

    this.notifyUserMessageLimitReached()
    return false
  }

  private notifyUserMessageLimitReached (): void {
    const limitMessage = `You've reached the limit of ${this.maxUserMessages} user messages for this conversation. Please restart the chat to continue.`
    const alreadyShown = this.messages.some(message =>
      message.role === ConversationRoles.ASSISTANT && message.content === limitMessage
    )
    if (alreadyShown) return

    this.messages.push({
      role: ConversationRoles.ASSISTANT,
      content: limitMessage
    } as ChatMessage)
    this.$emit('conversation-updated')
  }

  private initializeAILogic (): void {
    this.setupSystemPrompt()
    this.setupInitialConversation()
    this.setupTools()
    this.setupToolHandlers()
  }

  private setupSystemPrompt (): void {
    const defaultSystemPrompt = this.buildDefaultSystemPrompt()
    this.systemPromptFingerprint = this.buildSystemPromptFingerprint()
    this.combinedSystemPrompt = getCachedSystemPrompt(this.systemPromptFingerprint) || defaultSystemPrompt
  }

  private buildDefaultSystemPrompt (): string {
    const i18n = (this as any).$i18n
    const locale = i18n && i18n.locale ? i18n.locale : 'en-gb'
    return getSystemPrompt(
      this.addEquipmentTags(this.availableTags),
      this.maxNumberOfAddedExercises,
      locale,
      this.getCompactLibraryFacetCounts(this.$store.state.availableExercises)
    )
  }

  private buildSystemPromptFingerprint (): string {
    const i18n = (this as any).$i18n
    const locale = i18n && i18n.locale ? i18n.locale : 'en-gb'
    const filters = this.addEquipmentTags(this.availableTags)
    const librarySummary = this.getCompactLibraryFacetCounts(this.$store.state.availableExercises)
    return getSystemPromptFingerprint(filters, this.maxNumberOfAddedExercises, locale, librarySummary)
  }

  private setupInitialConversation (): void {
    this.initialConversation = [
      {
        role: 'system',
        content: this.combinedSystemPrompt
      },
      {
        role: 'assistant',
        content: `${this.$t('AI_ASSISTANT_GREETING')}`
      }
    ] as ChatMessage[]
    this.messages = this.initialConversation.slice()
  }

  private replaceSystemPromptInMessages (prompt: string): void {
    const replacePrompt = (messages: ChatMessage[]) => {
      const systemMessage = messages.find(message => message.role === 'system')
      if (systemMessage) {
        systemMessage.content = prompt
      } else {
        messages.unshift({ role: 'system', content: prompt } as ChatMessage)
      }
    }

    replacePrompt(this.initialConversation)
    replacePrompt(this.messages)
    if (this.lastRequestHistory) replacePrompt(this.lastRequestHistory)
  }

  private setupTools (): void {
    this.tools = [
      {
        type: 'function',
        function: {
          name: 'close_chat',
          description: 'Close chat when the user is done.',
          parameters: {
            type: 'object',
            properties: {
              farewell_message: {
                type: 'string',
                description: 'Optional goodbye text.'
              }
            },
            required: []
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'apply_filter_tags',
          description: 'Apply exercise filters and return a compact filtered-pool summary.',
          parameters: {
            type: 'object',
            properties: {
              body_area: { type: 'array', items: { type: 'string' }, description: 'BodyArea tags.' },
              movement_pattern: { type: 'array', items: { type: 'string' }, description: 'MovementPattern tags.' },
              focus: { type: 'array', items: { type: 'string' }, description: 'Focus tags.' },
              muscle_group: { type: 'array', items: { type: 'string' }, description: 'MuscleGroup tags.' },
              health_limitation: { type: 'array', items: { type: 'string' }, description: 'HealthLimitation tags.' },
              equipment: { type: 'array', items: { type: 'string' }, description: 'Equipment tags.' },
              category: { type: 'array', items: { type: 'string' }, description: 'Category tags.' },
              execution: { type: 'array', items: { type: 'string' }, description: 'Execution tags.' },
              joints: { type: 'array', items: { type: 'string' }, description: 'Joints tags.' },
              clinical_pictures: { type: 'array', items: { type: 'string' }, description: 'ClinicalPictures tags.' },
              range_of_motion: { type: 'array', items: { type: 'string' }, description: 'RangeOfMotion tags after ambiguity resolution.' },
              desired_duration_minutes: { type: 'number', description: 'Requested duration minutes.' }
            },
            required: ['desired_duration_minutes']
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'undo_tags',
          description: 'Remove exercise filters and return a compact filtered-pool summary.',
          parameters: {
            type: 'object',
            properties: {
              body_area: { type: 'array', items: { type: 'string' }, description: 'BodyArea tags.' },
              movement_pattern: { type: 'array', items: { type: 'string' }, description: 'MovementPattern tags.' },
              focus: { type: 'array', items: { type: 'string' }, description: 'Focus tags.' },
              muscle_group: { type: 'array', items: { type: 'string' }, description: 'MuscleGroup tags.' },
              health_limitation: { type: 'array', items: { type: 'string' }, description: 'HealthLimitation tags.' },
              equipment: { type: 'array', items: { type: 'string' }, description: 'Equipment tags.' },
              category: { type: 'array', items: { type: 'string' }, description: 'Category tags.' },
              execution: { type: 'array', items: { type: 'string' }, description: 'Execution tags.' },
              joints: { type: 'array', items: { type: 'string' }, description: 'Joints tags.' },
              clinical_pictures: { type: 'array', items: { type: 'string' }, description: 'ClinicalPictures tags.' },
              range_of_motion: { type: 'array', items: { type: 'string' }, description: 'RangeOfMotion tags.' }
            },
            required: []
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'build_workout_from_filtered',
          description: 'Build from the current filtered pool and return compact planner/workout summaries.',
          parameters: {
            type: 'object',
            properties: {
              target_duration_minutes: { type: 'number', description: 'Final duration minutes.' },
              workout_name: { type: 'string', description: 'Workout title.' },
              mode: { type: 'string', enum: ['replace', 'append'], description: 'replace or append.' },
              allow_repeated_exercises: { type: 'boolean', description: 'True only after repeat consent.' },
              constraints: {
                type: 'object',
                properties: {
                  goal: { type: 'string' },
                  body_area: { type: 'array', items: { type: 'string' } },
                  equipment: { type: 'array', items: { type: 'string' } },
                  requires_equipment: { type: 'boolean', description: 'Equipment allowed, none named.' },
                  health_limitation: { type: 'array', items: { type: 'string' } },
                  focus: { type: 'array', items: { type: 'string' } }
                }
              }
            },
            required: ['target_duration_minutes', 'workout_name', 'mode']
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'remove_exercises',
          description: 'Remove exercises by position or case-insensitive partial name.',
          parameters: {
            type: 'object',
            properties: {
              reasoning_summary: { type: 'string' },
              positions: { type: 'array', items: { type: 'number' }, description: '0-based positions.' },
              exercise_name: { type: 'string', description: 'Name or partial name.' }
            },
            required: []
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'remove_all_exercises',
          description: 'Clear the workout.',
          parameters: {
            type: 'object',
            required: []
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'set_workout_name',
          description: 'Set or update workout title.',
          parameters: {
            type: 'object',
            properties: {
              workout_name: { type: 'string' }
            },
            required: ['workout_name']
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'ask_question',
          description: 'Ask one clickable multiple-choice question; app adds Any.',
          parameters: {
            type: 'object',
            properties: {
              question: { type: 'string', description: 'Short direct question only.' },
              options: { type: 'array', items: { type: 'string' }, minItems: 2, description: 'At least two concise options; omit Any.' },
              required_range_of_motion: { type: 'boolean', description: 'True only for required range ambiguity.' }
            },
            required: ['question', 'options']
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'check_current_workout',
          description: 'Inspect workout summary; include exercise details only for edit/remove.',
          parameters: {
            type: 'object',
            properties: {
              include_exercises: { type: 'boolean', description: 'True when positions/names are needed.' }
            },
            required: []
          }
        }
      }
    ]
  }

  private setupToolHandlers (): void {
    this.toolHandlers = {
      close_chat: this.handleCloseChat.bind(this),
      apply_filter_tags: this.handleOutputTags.bind(this),
      undo_filter_tags: this.handleRemoveTags.bind(this),
      undo_tags: this.handleRemoveTags.bind(this),
      build_workout_from_filtered: this.handleBuildWorkoutFromFiltered.bind(this),
      remove_exercises: this.handleRemoveExercises.bind(this),
      remove_all_exercises: this.handleRemoveAllExercises.bind(this),
      set_workout_name: this.handleSetWorkoutName.bind(this),
      ask_question: this.handleAskQuestion.bind(this),
      check_current_workout: this.handleCheckCurrentWorkout.bind(this)
    }
  }

  private cleanupAILogic (): void {
    this.invalidateActiveRequest()
    this.cancelPendingQuestion()
    eventSystem.removeEventListener('filteredExercises', this.filterEventHandler)
  }

  private getLoadingMessageForTool (toolName: string): string {
    const toolMessages = {
      'ask_question': 'Awaiting Answer',
      'apply_filter_tags': 'Filtering',
      'undo_tags': 'Removing Filters',
      'build_workout_from_filtered': 'Building Workout',
      'remove_exercises': 'Removing Exercises',
      'remove_all_exercises': 'Clearing Exercises',
      'set_workout_name': 'Setting Workout Name',
      'check_current_workout': 'Checking Workout',
      'close_chat': 'Closing Chat'
    }
    return toolMessages[toolName] || 'Processing'
  }

  private getLoadingMessageFromInput (input: string): string {
    const lowerInput = input.toLowerCase()
    // Exercise removal keywords (check first, before 'add' because 'remove' is more specific)
    if (lowerInput.includes('remove') || lowerInput.includes('delete') || lowerInput.includes('clear') ||
        lowerInput.includes('take out')) {
      return 'Removing Exercises'
    }

    if (lowerInput.includes('workout') || lowerInput.includes('routine') || lowerInput.includes('session') ||
        lowerInput.includes('create') || lowerInput.includes('build')) {
      return 'Building Workout'
    }

    // Filter-related keywords
    if (lowerInput.includes('filter') || lowerInput.includes('show') || lowerInput.includes('find') ||
        lowerInput.includes('tag') || lowerInput.includes('equipment') || lowerInput.includes('body part')) {
      return 'Filtering'
    }

    // Exercise addition keywords
    if (lowerInput.includes('add') || lowerInput.includes('include') || lowerInput.includes('put') ||
        lowerInput.includes('exercise')) {
      return 'Adding Exercises'
    }

    // Exercise removal keywords
    // unreachable: the same keywords already returned 'Removing Exercises' above
    /* istanbul ignore if */
    if (lowerInput.includes('remove') || lowerInput.includes('delete') || lowerInput.includes('clear') ||
        lowerInput.includes('take out')) {
      return 'Removing Exercises'
    }

    // Workout naming keywords
    if (lowerInput.includes('name') || lowerInput.includes('call') || lowerInput.includes('title')) {
      return 'Setting Workout Name'
    }

    // Default
    return 'Thinking'
  }

  private createEmptyWorkoutRequestState (): WorkoutRequestState {
    return {
      duration: { value: null },
      category: { value: null },
      execution: { value: null },
      target: { value: null },
      equipment: { value: null },
      rangeOfMotion: { value: null }
    }
  }

  private normalizeIntakeValue (value: string): string {
    return (value || '').toString().trim().toLowerCase().replace(/[\s-]+/g, '_')
  }

  private normalizeIntakeSearchText (value: string): string {
    return ` ${(value || '').toString().trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/[^a-z0-9%]+/g, ' ')} `
  }

  private getKnownTagValues (tagType: string, fallback: string[]): string[] {
    const sources = [this.availableTags, this.exerciseTags]
    for (const source of sources) {
      if (source && Array.isArray(source[tagType]) && source[tagType].length) {
        return source[tagType].map(value => this.normalizeIntakeValue(value))
      }
    }
    return fallback
  }

  private hasValueForSlot (slot: WorkoutRequestSlot): boolean {
    const state = this.workoutRequestState[slot]
    return !!state && (state.noPreference || state.value !== null && state.value !== undefined && state.value !== '')
  }

  private getMissingRequiredWorkoutSlots (): WorkoutRequestSlot[] {
    return REQUIRED_WORKOUT_SLOTS.filter(slot => !this.hasValueForSlot(slot))
  }

  private getNextMissingRequiredWorkoutSlot (): WorkoutRequestSlot | null {
    const missingSlots = this.getMissingRequiredWorkoutSlots()
    return missingSlots.length ? missingSlots[0] : null
  }

  private hasMissingRequiredWorkoutSlots (): boolean {
    return this.getMissingRequiredWorkoutSlots().length > 0
  }

  private slotLabel (slot: WorkoutRequestSlot): string {
    const labels = {
      duration: 'duration',
      category: 'Category',
      execution: 'Execution',
      target: 'Joint, Muscle Group, or Clinical Picture',
      equipment: 'equipment',
      rangeOfMotion: 'Range of Motion'
    }
    return labels[slot]
  }

  private setWorkoutRequestSlot (slot: WorkoutRequestSlot, value: any, noPreference: boolean = false): void {
    this.workoutRequestState[slot] = {
      value,
      noPreference
    }
  }

  private updateWorkoutRequestStateFromText (text: string): void {
    const normalizedText = this.normalizeIntakeSearchText(text)
    const durationMatch = (text || '').match(/\b(\d+(?:[.,]\d+)?)\s*(?:-| )?(?:min|mins|minute|minutes)\b/i)
    if (durationMatch) {
      this.setWorkoutRequestSlot('duration', Number(durationMatch[1].replace(',', '.')))
    }

    const category = this.findKnownValueInText(normalizedText, this.getKnownTagValues('ExerciseTags::Category', DEFAULT_CATEGORIES))
    if (category) this.setWorkoutRequestSlot('category', category)

    const executions = this.findKnownValuesInText(normalizedText, this.getKnownTagValues('ExerciseTags::Execution', DEFAULT_EXECUTIONS))
    if (executions.length) this.setWorkoutRequestSlot('execution', executions)

    const targets = this.findKnownValuesInText(normalizedText, this.getKnownTargetValues())
    if (targets.length) this.setWorkoutRequestSlot('target', targets)

    if (normalizedText.indexOf(' no equipment ') !== -1 || normalizedText.indexOf(' without equipment ') !== -1) {
      this.setWorkoutRequestSlot('equipment', [NO_EQUIPMENT])
    } else if (normalizedText.indexOf(' any equipment ') !== -1 || normalizedText.indexOf(' with equipment ') !== -1) {
      this.setWorkoutRequestSlot('equipment', 'any', true)
    }

    const rangeOfMotion = this.findKnownValueInText(normalizedText, this.getKnownTagValues('ExerciseTags::RangeOfMotion', ['full', '50', '25']))
    if (rangeOfMotion) this.setWorkoutRequestSlot('rangeOfMotion', rangeOfMotion)
  }

  private updateWorkoutRequestStateFromToolArgs (args: any): void {
    if (!args) return
    if (args.desired_duration_minutes || args.target_duration_minutes) {
      this.setWorkoutRequestSlot('duration', Number(args.desired_duration_minutes || args.target_duration_minutes))
    }
    if (Array.isArray(args.category) && args.category.length) this.setWorkoutRequestSlot('category', args.category)
    if (Array.isArray(args.execution) && args.execution.length) this.setWorkoutRequestSlot('execution', args.execution)
    if (Array.isArray(args.range_of_motion) && args.range_of_motion.length) this.setWorkoutRequestSlot('rangeOfMotion', args.range_of_motion)
    if (Array.isArray(args.equipment) && args.equipment.length) this.setWorkoutRequestSlot('equipment', args.equipment)
    if (args.constraints && Array.isArray(args.constraints.equipment) && args.constraints.equipment.length) {
      this.setWorkoutRequestSlot('equipment', args.constraints.equipment)
    }
    if (args.constraints && args.constraints.requires_equipment) {
      this.setWorkoutRequestSlot('equipment', 'any', true)
    }

    const targetValues: any[] = []
    ;['joints', 'muscle_group', 'clinical_pictures'].forEach(key => {
      if (Array.isArray(args[key])) targetValues.push(...args[key])
    })
    if (targetValues.length) this.setWorkoutRequestSlot('target', targetValues)
  }

  private getHealthLimitationValues (args: any): string[] {
    if (!args) return []
    const values: any[] = []
    if (Array.isArray(args.health_limitation)) values.push(...args.health_limitation)
    if (args.constraints && Array.isArray(args.constraints.health_limitation)) {
      values.push(...args.constraints.health_limitation)
    }
    return values
      .map(value => this.normalizeIntakeValue(value))
      .filter(value => !!value && value !== 'none' && value !== 'no_limitations')
  }

  private addActiveHealthLimitations (values: string[]): void {
    values.forEach(value => this.activeHealthLimitations.add(value))
  }

  private removeActiveHealthLimitations (values: string[]): void {
    values.forEach(value => this.activeHealthLimitations.delete(value))
  }

  private mergeActiveHealthLimitationsIntoConstraints (constraints: any): any {
    const merged = { ...(constraints || {}) }
    const healthLimitations = new Set<string>(this.getHealthLimitationValues({ constraints: merged }))
    this.activeHealthLimitations.forEach(value => healthLimitations.add(value))
    if (healthLimitations.size) {
      merged.health_limitation = Array.from(healthLimitations)
    }
    return merged
  }

  private updateWorkoutRequestStateFromQuestionAnswer (question: QuestionData | null, answer: string): void {
    if (!question || !question.slot) return
    const anyOption = `${this.$t('AI_ASSISTANT_ANY_OPTION')}`
    const selectedAny = answer.trim().toLocaleLowerCase() === anyOption.trim().toLocaleLowerCase()
    this.setWorkoutRequestSlot(question.slot, selectedAny ? null : answer, selectedAny)
  }

  private findKnownValueInText (normalizedText: string, values: string[]): string | null {
    const matches = this.findKnownValuesInText(normalizedText, values)
    return matches.length ? matches[0] : null
  }

  private findKnownValuesInText (normalizedText: string, values: string[]): string[] {
    return values
      .map(value => this.normalizeIntakeValue(value))
      .filter(value => {
        const searchValue = value.replace(/_/g, ' ')
        return !!value && normalizedText.indexOf(` ${searchValue} `) !== -1
      })
  }

  private getKnownTargetValues (): string[] {
    return Array.from(new Set(
      this.getKnownTagValues('ExerciseTags::MuscleGroup', DEFAULT_TARGETS)
        .concat(this.getKnownTagValues('ExerciseTags::Joints', DEFAULT_TARGETS))
        .concat(this.getKnownTagValues('ExerciseTags::ClinicalPictures', DEFAULT_TARGETS))
        .concat(DEFAULT_TARGETS)
    ))
  }

  private inferQuestionSlot (args: any): WorkoutRequestSlot | null {
    if (args && args.required_range_of_motion) return 'rangeOfMotion'
    const question = this.normalizeIntakeValue(args && args.question ? args.question : '')
    const options = Array.isArray(args && args.options)
      ? args.options.map(option => this.normalizeIntakeValue(option)).join(' ')
      : ''
    const combined = `${question} ${options}`

    if (combined.indexOf('duration') !== -1 || combined.indexOf('how_long') !== -1 || combined.indexOf('minutes') !== -1) return 'duration'
    if (combined.indexOf('category') !== -1 || this.hasOnlyKnownOptions(options, this.getKnownTagValues('ExerciseTags::Category', DEFAULT_CATEGORIES))) return 'category'
    if (combined.indexOf('execution') !== -1 || this.hasOnlyKnownOptions(options, this.getKnownTagValues('ExerciseTags::Execution', DEFAULT_EXECUTIONS))) return 'execution'
    if (combined.indexOf('equipment') !== -1) return 'equipment'
    if (combined.indexOf('joint') !== -1 || combined.indexOf('muscle') !== -1 || combined.indexOf('clinical') !== -1) return 'target'
    return null
  }

  private hasOnlyKnownOptions (optionsText: string, knownValues: string[]): boolean {
    if (!optionsText.trim().length) return false
    const optionValues = optionsText.split(/\s+/).filter(value => !!value)
    // a text that is not blank always has at least one value
    /* istanbul ignore if */
    if (!optionValues.length) return false
    return optionValues.every(value => knownValues.indexOf(value) !== -1)
  }

  private isUnsupportedOptionalQuestion (args: any, slot: WorkoutRequestSlot | null): boolean {
    if (slot) return false
    const question = this.normalizeIntakeValue(args && args.question ? args.question : '')
    return ['goal', 'focus', 'difficulty', 'level', 'phase'].some(value => question.indexOf(value) !== -1)
  }

  // Tool Handlers
  private handleCloseChat (args: any): string {
    // Clear any loading states through callbacks before closing
    if (this.loadingCallbacks && this.loadingCallbacks.onLoadingStopped) {
      this.loadingCallbacks.onLoadingStopped()
    }
    this.invalidateActiveRequest()
    this.restartConversation(false)
    if (this.closeChat) {
      this.closeChat()
    }
    return args.farewell_message || ''
  }

  private async handleOutputTags (args: any): Promise<string> {
    // Remove any existing name filter before applying tag filters
    this.removeFilterByName('')
    this.updateWorkoutRequestStateFromToolArgs(args)
    this.addActiveHealthLimitations(this.getHealthLimitationValues(args))

    if (args.desired_duration_minutes) {
      this.userRequestedDurationMinutes = args.desired_duration_minutes
    }
    const mapped = this.mapToolArgsToExerciseTags(args)
    if (mapped['ExerciseTags::RangeOfMotion']) {
      if (this.hasNoRangeOfMotionPreference) {
        delete mapped['ExerciseTags::RangeOfMotion']
      } else {
        this.allowedRangeOfMotionAmbiguitySignature = null
      }
    }
    if (Object.keys(mapped).length === 0) {
      return this.createExerciseListResponse(this.availableExercises.length)
    }
    const filterEvent = this.waitForFilterEvent('filteredExercises')
    this.parseJSONTags(mapped)
    const currentlyFilteredExercisesNumber = await filterEvent
    return this.createExerciseListResponse(currentlyFilteredExercisesNumber)
  }

  private async handleRemoveTags (args: any): Promise<string> {
    // Remove any existing name filter before modifying tag filters
    this.removeFilterByName('')
    this.removeActiveHealthLimitations(this.getHealthLimitationValues(args))

    const mapped = this.mapToolArgsToExerciseTags(args)
    if (Object.keys(mapped).length === 0) {
      return this.createExerciseListResponse(this.availableExercises.length)
    }
    console.log('Removing tags:', this.shouldLogDetailedAI() ? mapped : '[redacted]')
    const filterEvent = this.waitForFilterEvent('filteredExercises')
    const removedAny = this.removeJSONTags(mapped)
    const currentlyFilteredExercisesNumber = removedAny
      ? await filterEvent
      : this.availableExercises.length
    console.log(`After removal, filtered exercises: ${currentlyFilteredExercisesNumber}`)
    return this.createExerciseListResponse(currentlyFilteredExercisesNumber)
  }

  private async handleBuildWorkoutFromFiltered (args: any): Promise<string> {
    this.updateWorkoutRequestStateFromToolArgs(args)
    this.addActiveHealthLimitations(this.getHealthLimitationValues(args))
    const targetDurationMinutes = Number(args.target_duration_minutes)
    const mode: WorkoutPlannerMode = args.mode === 'append' ? 'append' : 'replace'
    const requestedWorkoutName = typeof args.workout_name === 'string'
      ? args.workout_name.trim()
      : ''
    const workoutName = requestedWorkoutName && requestedWorkoutName.toLowerCase() !== UNTITLED_WORKOUT_NAME
      ? requestedWorkoutName
      : `${targetDurationMinutes}-Minute Workout`

    if (!targetDurationMinutes || !isFinite(targetDurationMinutes) || targetDurationMinutes < 1 || targetDurationMinutes > 180) {
      return JSON.stringify({
        success: false,
        error: 'target_duration_minutes must be between 1 and 180.'
      })
    }

    const availableExercises = Array.isArray(this.availableExercises) ? this.availableExercises : []
    const rangeOfMotionAmbiguity = this.getRangeOfMotionAmbiguity(availableExercises)
    if (!this.hasNoRangeOfMotionPreference &&
        rangeOfMotionAmbiguity &&
        rangeOfMotionAmbiguity.signature !== this.allowedRangeOfMotionAmbiguitySignature) {
      return this.createRangeOfMotionAmbiguityResponse(rangeOfMotionAmbiguity)
    }
    const planningWorkout = new WorkoutData({
      ...this.workout,
      continuousWorkout: true,
      circleWorkout: false,
      stationLocked: false
    })
    const currentDurationSeconds = this.computeWorkoutDurationSeconds(planningWorkout)
    const plannerConstraints = this.mergeActiveHealthLimitationsIntoConstraints(args.constraints || {})
    const plannerResult = planWorkout({
      exercises: availableExercises,
      club: this.club || null,
      workout: planningWorkout,
      target_duration_minutes: targetDurationMinutes,
      workout_name: workoutName,
      mode,
      constraints: plannerConstraints,
      allow_repeated_exercises: args.allow_repeated_exercises === true,
      current_duration_seconds: currentDurationSeconds
    })

    if (!plannerResult.success) {
      const failureResult = {
        success: false,
        planner: this.getPlannerToolSummary(plannerResult),
        current_workout: this.getCompactCurrentWorkoutSummary(false)
      }
      this.lastBuildStatus = failureResult
      return JSON.stringify(failureResult)
    }

    const plannedExerciseIds = plannerResult.selections.map(selection => selection.exercise_id)
    const missingExerciseIds = plannedExerciseIds.filter(id => !availableExercises.find(exercise => exercise.id === id))
    // the planner only selects exercises from the list it was given
    /* istanbul ignore if */
    if (missingExerciseIds.length) {
      return JSON.stringify({
        success: false,
        error: 'Planner selected exercises that are no longer available.',
        current_workout: this.getCompactCurrentWorkoutSummary(false)
      })
    }

    if (!this.isRequestActive(this.currentToolRequestId)) return JSON.stringify({ success: false, error: 'Request was cancelled.' })
    const plannedExercises: ExerciseResponse[] = []
    for (const selection of plannerResult.selections) {
      if (!this.isRequestActive(this.currentToolRequestId)) return JSON.stringify({ success: false, error: 'Request was cancelled.' })
      const exercise = availableExercises.find(item => item.id === selection.exercise_id)
      // the planner only selects exercises from the list it was given
      /* istanbul ignore if */
      if (!exercise) continue
      for (let entry = 0; entry < selection.count; entry++) {
        plannedExercises.push(exercise)
      }
    }

    if (!this.isRequestActive(this.currentToolRequestId)) return JSON.stringify({ success: false, error: 'Request was cancelled.' })
    this.$store.commit('setGeneratedWorkoutFromExercises', {
      exercises: plannedExercises,
      mode,
      workoutName,
      continuousWorkout: true,
      circleWorkout: false,
      stationLocked: false
    })
    this.exercisesAddedInCurrentResponse = true

    const successResult = {
      success: true,
      workout_name: workoutName,
      planner: this.getPlannerToolSummary(plannerResult),
      current_workout: this.getCompactCurrentWorkoutSummary(false),
      message: 'Workout built successfully.'
    }
    this.lastBuildStatus = {
      success: true,
      workout_name: workoutName,
      planner: successResult.planner,
      current_workout: successResult.current_workout
    }
    return JSON.stringify(successResult)
  }

  private getAvailableTools (): any[] {
    if (this.hasMissingRequiredWorkoutSlots() ||
        this.hasUnresolvedRangeOfMotionAmbiguity() ||
        (!this.hasValueForSlot('equipment') && this.followUpQuestionCount < 2)) return this.tools
    return this.tools.filter(tool => tool.function.name !== 'ask_question')
  }

  private handleRemoveExercises (args: any): string {
    console.log('handleRemoveExercises called with args:', this.shouldLogDetailedAI() ? args : '[redacted]')
    const errors: string[] = []
    const removedPositions: number[] = []

    // Resolve target positions from explicit positions or an exercise name.
    let positions: number[] = Array.isArray(args.positions) ? [...args.positions] : []

    // Map name (partial, case-insensitive) to positions
    if ((!positions || positions.length === 0) && typeof args.exercise_name === 'string' && args.exercise_name.trim().length > 0) {
      const needle = args.exercise_name.trim().toLowerCase()
      const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
      const nn = normalize(needle)
      if (this.shouldLogDetailedAI()) {
        console.log('Searching for exercise name:', needle, 'normalized:', nn)
        console.log('Available exercises:', this.selectedWorkoutExercises.map((we, idx) => ({
          idx,
          name: `${this.$t('EXERCISE.' + we.exercise.number + '.NAME')}`,
          normalized: normalize(`${this.$t('EXERCISE.' + we.exercise.number + '.NAME')}`)
        })))
      }
      positions = this.selectedWorkoutExercises
        .map((we, idx) => ({ idx, name: `${this.$t('EXERCISE.' + we.exercise.number + '.NAME')}` }))
        .filter(x => {
          const n = normalize((x.name || '').toString())
          return n.includes(nn) || nn.split(' ').every(part => n.includes(part))
        })
        .map(x => x.idx)
      console.log('Matched positions:', positions)
    }

    // Validate positions
    if (!Array.isArray(positions) || positions.length === 0) {
      console.warn('No matching exercises found to remove')
      return JSON.stringify({
        success: false,
        error: 'No matching exercises to remove. Provide positions or exercise_name.'
      })
    }

    // Sort positions in descending order to avoid index shifting issues when removing
    const sortedPositions = Array.from(new Set(positions)).sort((a, b) => b - a)

    // Validate all positions are within bounds
    const maxPosition = this.selectedWorkoutExercises.length - 1
    for (const position of sortedPositions) {
      if (position < 0 || position > maxPosition) {
        errors.push(`Position ${position} is out of bounds (0-${maxPosition})`)
      }
    }

    if (errors.length === 0) {
      // Remove exercises by position (from highest index to lowest to avoid shifting issues)
      for (const position of sortedPositions) {
        try {
          this.$store.commit('removeExerciseFromWorkout', position)
          removedPositions.push(position)
          console.log('Removed exercise at position:', position)
        } catch (error) {
          errors.push(`Failed to remove exercise at position ${position}: ${error.message}`)
          console.error('Failed to remove exercise at position:', position, error)
        }
      }
    }

    const result = JSON.stringify({
      success: errors.length === 0,
      message: removedPositions.length > 0 ? `${removedPositions.length} exercise(s) removed from workout` : 'No exercises removed',
      removed_positions: removedPositions.sort((a, b) => a - b),
      errors: errors.length > 0 ? errors : undefined,
      current_workout: this.getCompactCurrentWorkoutSummary(false)
    })
    console.log('handleRemoveExercises returning:', this.shouldLogDetailedAI() ? result : '[redacted]')
    return result
  }

  private handleRemoveAllExercises (): string {
    this.$store.commit('removeExercisesFromWorkout')
    return JSON.stringify({
      success: true,
      message: 'All exercises removed from workout',
      current_workout: this.getCompactCurrentWorkoutSummary(false)
    })
  }

  private handleSetWorkoutName (args: any): string {
    this.$store.state.new_workout.name = args.workout_name
    return JSON.stringify({
      success: true,
      workout_name: args.workout_name,
      message: `Workout name set to: ${args.workout_name}`
    })
  }

  private handleAskQuestion (args: any): Promise<string> {
    const slot = this.inferQuestionSlot(args)
    const ambiguity = args.required_range_of_motion && !this.hasNoRangeOfMotionPreference
      ? this.getRangeOfMotionAmbiguity(this.availableExercises)
      : null
    const isRequiredRangeOfMotionQuestion = !!ambiguity &&
      ambiguity.signature !== this.allowedRangeOfMotionAmbiguitySignature

    if (args.required_range_of_motion && !isRequiredRangeOfMotionQuestion) {
      return Promise.resolve(JSON.stringify({
        success: false,
        error: 'No unresolved range-of-motion ambiguity is available.'
      }))
    }

    if (this.isUnsupportedOptionalQuestion(args, slot)) {
      return Promise.resolve(JSON.stringify({
        success: false,
        error: 'This optional question is not required. Proceed with the available workout request information.'
      }))
    }

    if (slot && slot !== 'rangeOfMotion' && this.hasValueForSlot(slot)) {
      const nextSlot = this.getNextMissingRequiredWorkoutSlot()
      return Promise.resolve(JSON.stringify({
        success: false,
        error: nextSlot
          ? `${this.slotLabel(slot)} is already known. Ask only for missing ${this.slotLabel(nextSlot)} next.`
          : `${this.slotLabel(slot)} is already known. Proceed with filtering and workout generation.`
      }))
    }

    if (slot === 'equipment' && this.hasMissingRequiredWorkoutSlots()) {
      const nextSlot = this.getNextMissingRequiredWorkoutSlot()
      return Promise.resolve(JSON.stringify({
        success: false,
        error: `Ask for missing ${this.slotLabel(nextSlot as WorkoutRequestSlot)} before equipment.`
      }))
    }

    const isMandatorySlotQuestion = !!slot && REQUIRED_WORKOUT_SLOTS.indexOf(slot) !== -1
    if (!isRequiredRangeOfMotionQuestion && !isMandatorySlotQuestion && this.followUpQuestionCount >= 2) {
      return Promise.resolve(JSON.stringify({
        success: false,
        error: 'The maximum of two follow-up questions has been reached. Proceed with the available information.'
      }))
    }

    const suppliedOptions = isRequiredRangeOfMotionQuestion
      ? (ambiguity as RangeOfMotionAmbiguity).options
      : Array.isArray(args.options)
      ? args.options.filter(option => typeof option === 'string' && option.trim().length)
      : []
    const normalizedOptions = this.normalizeQuestionOptionsForSlot(slot, suppliedOptions)
    if (normalizedOptions.length < 2) {
      return Promise.resolve(JSON.stringify({
        success: false,
        error: 'ask_question requires at least two valid clickable options for the requested field.'
      }))
    }

    const anyOption = `${this.$t('AI_ASSISTANT_ANY_OPTION')}`
    const normalizedAnyOption = anyOption.trim().toLocaleLowerCase()
    const options = normalizedOptions
      .filter(option => option.trim().toLocaleLowerCase() !== normalizedAnyOption)
      .concat(anyOption)

    if (!isRequiredRangeOfMotionQuestion) {
      this.followUpQuestionCount++
    }
    return new Promise((resolve) => {
      this.currentQuestion = {
        question: args.question,
        options,
        requiredRangeOfMotion: isRequiredRangeOfMotionQuestion,
        rangeOfMotionSignature: ambiguity ? ambiguity.signature : undefined,
        slot: isRequiredRangeOfMotionQuestion ? 'rangeOfMotion' : slot || undefined
      }
      this.questionResponseResolver = resolve
      this.$emit('question-asked')
    })
  }

  private handleCheckCurrentWorkout (args: any = {}): string {
    const summary = this.getCompactCurrentWorkoutSummary(args && args.include_exercises === true)
    return JSON.stringify({
      success: true,
      ...summary,
      message: `Current workout has ${summary.total_exercise_count} exercises with total duration of ${summary.total_duration_minutes} minutes`
    })
  }

  // AI Conversation Methods
  private cleanConversationHistory (): ChatMessage[] {
    let history = this.messages.filter(msg =>
      msg &&
      !msg.uiOnly &&
      ((msg.content || '').trim() !== '' || msg.tool_calls || msg.role === 'tool')
    )

    this.validateHistoryRoles(history)
    return this.filterRelevantContext(history)
  }

  private filterRelevantContext (history: ChatMessage[]): ChatMessage[] {
    const systemMessage = history.find(msg => msg.role === 'system')
    const latestUserMessage = this.getLatestMessageByRole(history, ConversationRoles.USER)
    const relevantToolMessages = this.getRelevantToolMessages(history)

    const filtered: ChatMessage[] = []
    if (systemMessage) filtered.push(systemMessage)
    filtered.push(this.createCompactStateMessage())

    const contextualMessages = relevantToolMessages.slice()
    if (latestUserMessage && contextualMessages.indexOf(latestUserMessage) === -1) {
      contextualMessages.push(latestUserMessage)
    }

    filtered.push(...contextualMessages
      .filter((msg, index, messages) => messages.indexOf(msg) === index)
      .sort((a, b) => history.indexOf(a) - history.indexOf(b))
      .filter(msg => !filtered.includes(msg)))
    return filtered
  }

  private getLatestMessageByRole (history: ChatMessage[], role: ConversationRoles): ChatMessage | null {
    for (let index = history.length - 1; index >= 0; index--) {
      if (history[index].role === role) return history[index]
    }
    return null
  }

  private createCompactStateMessage (): ChatMessage {
    return {
      role: ConversationRoles.SYSTEM,
      content: `Compact conversation state: ${JSON.stringify(this.getCompactConversationState())}`
    } as ChatMessage
  }

  private getCompactConversationState (): any {
    return {
      known_slots: this.getCompactWorkoutRequestState(),
      missing_required_slots: this.getMissingRequiredWorkoutSlots(),
      follow_up_questions_used: this.followUpQuestionCount,
      selected_filters: this.getSelectedFilterSummary(),
      range_of_motion: {
        no_preference: this.hasNoRangeOfMotionPreference,
        unresolved_ambiguity: this.hasUnresolvedRangeOfMotionAmbiguity()
      },
      active_health_limitations: Array.from(this.activeHealthLimitations),
      workout: this.getCompactCurrentWorkoutSummary(false),
      last_build: this.lastBuildStatus
    }
  }

  private getCompactWorkoutRequestState (): any {
    return Object.keys(this.workoutRequestState).reduce((acc, slot: WorkoutRequestSlot) => {
      const state = this.workoutRequestState[slot]
      if (state && (state.noPreference || state.value !== null && state.value !== undefined && state.value !== '')) {
        acc[slot] = state.noPreference ? 'Any' : state.value
      }
      return acc
    }, {})
  }

  private getSelectedFilterSummary (): any {
    const selectedFilters = this.$store.state.exerciseFilters && this.$store.state.exerciseFilters.tags
    if (!selectedFilters) return {}

    if (selectedFilters instanceof Set) {
      return { tags: Array.from(selectedFilters) }
    }

    return Object.keys(selectedFilters).reduce((summary, key) => {
      const values = selectedFilters[key]
      if (values instanceof Set) {
        const list = Array.from(values)
        if (list.length) summary[key] = list
      } else if (Array.isArray(values) && values.length) {
        summary[key] = values
      }
      return summary
    }, {})
  }

  private getRelevantToolMessages (history: ChatMessage[]): ChatMessage[] {
    const foundTools: {
      apply_filter_tags: { message: ChatMessage; index: number } | null,
      build_workout_from_filtered: { message: ChatMessage; index: number } | null,
      remove_exercises: { message: ChatMessage; index: number } | null,
      set_workout_name: { message: ChatMessage; index: number } | null,
      check_current_workout: { message: ChatMessage; index: number } | null
    } = {
      apply_filter_tags: null,
      build_workout_from_filtered: null,
      remove_exercises: null,
      set_workout_name: null,
      check_current_workout: null
    }

    // Find most recent relevant tool calls (scanning backwards)
    for (let i = history.length - 1; i >= 0; i--) {
      const msg = history[i]
      if (msg.role === 'assistant' && msg.tool_calls) {
        for (const toolCall of msg.tool_calls) {
          const toolName = toolCall.function && toolCall.function.name
          if (!toolName) continue

          // Only keep most recent filter application
          if (toolName === 'apply_filter_tags' && !foundTools.apply_filter_tags) {
            foundTools.apply_filter_tags = { message: msg, index: i }
          }

          if (toolName === 'build_workout_from_filtered' && !foundTools.build_workout_from_filtered) {
            foundTools.build_workout_from_filtered = { message: msg, index: i }
          }

          // Only keep most recent removals and name setting
          if (toolName === 'remove_exercises' && !foundTools.remove_exercises) {
            foundTools.remove_exercises = { message: msg, index: i }
          }
          if (toolName === 'set_workout_name' && !foundTools.set_workout_name) {
            foundTools.set_workout_name = { message: msg, index: i }
          }
          if (toolName === 'check_current_workout' && !foundTools.check_current_workout) {
            foundTools.check_current_workout = { message: msg, index: i }
          }
        }
      }
    }

    // Collect relevant messages with their tool responses
    const relevant: ChatMessage[] = []
    const processedIndices = new Set<number>()

    // Flatten and filter all found tools
    const allFoundTools: { message: ChatMessage; index: number }[] = []
    if (foundTools.apply_filter_tags) allFoundTools.push(foundTools.apply_filter_tags)
    if (foundTools.build_workout_from_filtered) allFoundTools.push(foundTools.build_workout_from_filtered)
    if (foundTools.remove_exercises) allFoundTools.push(foundTools.remove_exercises)
    if (foundTools.set_workout_name) allFoundTools.push(foundTools.set_workout_name)
    if (foundTools.check_current_workout) allFoundTools.push(foundTools.check_current_workout)

    allFoundTools.forEach(({ message, index }) => {
      if (!processedIndices.has(index)) {
        processedIndices.add(index)
        relevant.push(message)

        // Add ALL corresponding tool responses for this message
        // only messages with tool calls are collected above
        /* istanbul ignore else */
        if (message.tool_calls) {
          const expectedResponseIds = message.tool_calls.map(call => call.id)
          const toolResponses: ChatMessage[] = []

          // Find all tool responses that match this message's tool calls
          for (let j = index + 1; j < history.length; j++) {
            const potentialResponse = history[j]
            if (potentialResponse.role === 'tool' &&
                potentialResponse.tool_call_id &&
                expectedResponseIds.includes(potentialResponse.tool_call_id)) {
              toolResponses.push(potentialResponse)
            }
            // Stop looking once we hit another assistant message
            if (potentialResponse.role === 'assistant') break
          }

          // Only include if we have ALL expected responses
          if (toolResponses.length === expectedResponseIds.length) {
            relevant.push(...toolResponses)
          } else {
            // Remove the tool call message if we don't have complete responses
            relevant.pop()
          }
        }
      }
    })

    // Sort by original order in conversation
    const sortedRelevant = relevant.sort((a, b) => {
      const aIndex = history.indexOf(a)
      const bIndex = history.indexOf(b)
      return aIndex - bIndex
    })

    // Validate tool call/response pairing before returning
    return this.validateToolCallResponsePairing(sortedRelevant)
  }

  private validateToolCallResponsePairing (messages: ChatMessage[]): ChatMessage[] {
    const validated: ChatMessage[] = []

    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i]

      if (msg.role === 'assistant' && msg.tool_calls) {
        const expectedResponseIds = msg.tool_calls.map(call => call.id)
        const toolResponses: ChatMessage[] = []

        // Look for all tool responses immediately following this message
        let j = i + 1
        while (j < messages.length && messages[j].role === 'tool') {
          const toolMsg = messages[j]
          if (toolMsg.tool_call_id && expectedResponseIds.includes(toolMsg.tool_call_id)) {
            toolResponses.push(toolMsg)
          }
          j++
        }

        // Only include if we have complete pairing
        if (toolResponses.length === expectedResponseIds.length) {
          validated.push(msg)
          validated.push(...toolResponses)
          i = j - 1 // Skip the tool responses we just added
        }
        // Otherwise skip this tool call message entirely
      } else if (msg.role !== 'tool') {
        // Include non-tool messages (but skip orphaned tool messages)
        validated.push(msg)
      }
    }

    console.log('Context validation:')
    console.log('Original messages:', messages.length)
    console.log('Validated messages:', validated.length)
    console.log('Tool call count:', validated.filter(m => m.tool_calls).length)
    console.log('Tool response count:', validated.filter(m => m.role === 'tool').length)

    return validated
  }

  private validateHistoryRoles (history: ChatMessage[]): void {
    for (let i = 0; i < history.length - 1; i++) {
      const currentRole = history[i].role
      const nextRole = history[i + 1].role

      if (currentRole === 'tool' && nextRole === 'user') {
        console.error(`ROLE SEQUENCE ERROR: tool message at index ${i} followed by user message at index ${i + 1}`)
        console.error(`Tool message:`, this.getSafeLogMessage(history[i]))
        console.error(`User message:`, this.getSafeLogMessage(history[i + 1]))

        console.log('Attempting to fix by removing tool message...')
        history.splice(i, 1)
        i--
      }
    }
  }

  private addRetryInstruction (history: ChatMessage[], instruction: string): void {
    const systemIndex = history.findIndex(message => message.role === ConversationRoles.SYSTEM)
    const retrySystemMessage = {
      role: ConversationRoles.SYSTEM,
      content: `Retry instruction: ${instruction}`
    } as ChatMessage

    for (let index = history.length - 1; index >= 0; index--) {
      const message = history[index]
      const content = message && message.content ? message.content : ''
      if (message &&
          message.role === ConversationRoles.SYSTEM &&
          content.indexOf('Retry instruction:') === 0) {
        history.splice(index, 1)
      }
    }
    history.splice(systemIndex >= 0 ? systemIndex + 1 : 0, 0, retrySystemMessage)
  }

  private startRequest (): number {
    this.activeRequestId++
    return this.activeRequestId
  }

  private invalidateActiveRequest (): void {
    this.activeRequestId++
  }

  private isRequestCurrent (requestId: number): boolean {
    return requestId === this.activeRequestId
  }

  private isRequestActive (requestId: number): boolean {
    return this.isRequestCurrent(requestId) && !this.conversationStopped
  }

  private async fetchAIResponse (internal: boolean = false): Promise<string> {
    const requestId = this.startRequest()
    const history = this.cleanConversationHistory()
    this.lastRequestHistory = history.slice()
    try {
      return await this.processAIConversation(history, internal, requestId)
    } catch (error) {
      if (this.isRequestActive(requestId)) this.handleAIResponseError(error)
      return ''
    } finally {
      if (this.isRequestCurrent(requestId)) this.logConversationTurn()
    }
  }

  private logConversationTurn (): void {
    this.conversationTurnNumber++
    const conversation = this.messages.filter(message => !!message).map(message => ({
      role: message.role,
      content: this.getLogContent(message.content),
      tool_call_id: message.tool_call_id,
      tool_calls: this.getLogToolCalls(message.tool_calls)
    }))
    const toolCalls = conversation.reduce((calls: any[], message: any) => {
      if (!Array.isArray(message.tool_calls)) return calls

      message.tool_calls.forEach((toolCall: any) => {
        const toolResponse = conversation.find((candidate: any) =>
          candidate.role === ConversationRoles.TOOL && candidate.tool_call_id === toolCall.id
        )
        calls.push({
          id: toolCall.id,
          name: toolCall.function && toolCall.function.name,
          arguments: toolCall.function && toolCall.function.arguments,
          result: toolResponse ? toolResponse.content : undefined
        })
      })
      return calls
    }, [])

    console.log(`[PixFit AI] Conversation turn ${this.conversationTurnNumber}`, {
      conversation,
      toolCalls
    })
  }

  private shouldLogDetailedAI (): boolean {
    return NODE_ENV !== 'production'
  }

  private getLogContent (content: string | null): string | null {
    if (this.shouldLogDetailedAI()) return content
    return content && content.trim().length ? '[redacted]' : content
  }

  private getLogToolCalls (toolCalls: any[] | undefined): any[] | undefined {
    if (!toolCalls || this.shouldLogDetailedAI()) return toolCalls
    return toolCalls.map(toolCall => ({
      id: toolCall.id,
      type: toolCall.type,
      function: {
        name: toolCall.function && toolCall.function.name,
        arguments: '[redacted]'
      }
    }))
  }

  private getSafeLogMessage (message: ChatMessage): any {
    return {
      role: message.role,
      content: this.getLogContent(message.content),
      tool_call_id: message.tool_call_id,
      tool_calls: this.getLogToolCalls(message.tool_calls)
    }
  }

  private formatReportMessage (message: ChatMessage, index: number): string {
    if (message.role === ConversationRoles.ASSISTANT && message.tool_calls && message.tool_calls.length) {
      return [
        `${index}. assistant tool request:`,
        message.tool_calls.map(toolCall => {
          const toolName = toolCall.function && toolCall.function.name ? toolCall.function.name : 'unknown_tool'
          const args = toolCall.function && toolCall.function.arguments ? toolCall.function.arguments : '{}'
          return `- ${toolName}: ${args}`
        }).join('\n')
      ].join('\n')
    }

    if (message.role === ConversationRoles.TOOL) {
      return [
        `${index}. tool response${message.tool_call_id ? ` (${message.tool_call_id})` : ''}:`,
        message.content || ''
      ].join('\n')
    }

    return [
      `${index}. ${message.role}:`,
      message.content || ''
    ].join('\n')
  }

  private getSelectedExerciseNames (): string[] {
    return (this.selectedWorkoutExercises || [])
      .map((workoutExercise, index) => {
        if (!workoutExercise || !workoutExercise.exercise) return ''
        const name = this.getExerciseDisplayName(workoutExercise.exercise)
        return `${index + 1}. ${name}`
      })
      .filter(name => !!name)
  }

  private createReportFingerprint (conversation: string, selectedExercises: string[]): string {
    const source = JSON.stringify({
      conversation,
      selectedExercises
    })
    let hash = 0
    for (let i = 0; i < source.length; i++) {
      hash = ((hash << 5) - hash) + source.charCodeAt(i)
      hash = hash & hash
    }
    return Math.abs(hash).toString(36)
  }

  private async processAIConversation (history: ChatMessage[], internal: boolean, requestId: number = this.activeRequestId): Promise<string> {
    const model = 'mistral-medium-2505'

    this.exercisesAddedInCurrentResponse = false
    this.allowPlainTextResponse = false

    let placeholderIndex = this.messages.push({ role: 'assistant', content: '' } as ChatMessage) - 1
    this.$emit('message-added')

    const userIndex = internal ? this.messages.length - 2 : -1
    let toolLoopCount = 0
    let invalidPlainTextRetries = 0
    let nextToolChoice: AIToolChoice | null = null

    for (;;) {
      if (!this.isRequestActive(requestId)) return ''
      const availableTools = this.getAvailableTools()
      const aiChatBot = new AiChatBot(availableTools, this.toolHandlers, model)
      const toolChoice: AIToolChoice = nextToolChoice || (this.allowPlainTextResponse ? 'none' : 'any')
      nextToolChoice = null
      const streamResult = await this.getAIStreamResponse(aiChatBot, history, placeholderIndex, toolChoice, requestId)

      if (!this.isRequestActive(requestId)) return ''
      if (!streamResult) {
        this.finalizePlainTextResponse('I could not complete the workout request. Please try again with a broader workout request.', placeholderIndex)
        break
      }

      if (typeof streamResult === 'string') {
        if (this.isQuestionLikeResponse(streamResult)) {
          invalidPlainTextRetries++
          if (invalidPlainTextRetries > 2) {
            this.finalizePlainTextResponse('The assistant could not present the required selectable options. Restart the chat to try again.', placeholderIndex)
            break
          }

          this.messages[placeholderIndex].content = ''
          const retryInstruction = this.followUpQuestionCount < 2 && !this.allowPlainTextResponse
            ? 'The previous response was an invalid plain-text question. Ask the question only by calling ask_question with useful clickable options.'
            : 'The previous response ended with a question. Questions are no longer allowed. Respond with a short declarative confirmation and equipment summary.'
          this.addRetryInstruction(history, retryInstruction)
          nextToolChoice = this.followUpQuestionCount < 2 && !this.allowPlainTextResponse
            ? { type: 'function', function: { name: 'ask_question' } }
            : 'none'
          continue
        }
        this.finalizePlainTextResponse(streamResult, placeholderIndex)
        break
      } else {
        const toolCalls = streamResult.tool_calls || []
        if (!toolCalls.length) {
          this.finalizePlainTextResponse(
            streamResult.content && streamResult.content.trim().length
              ? streamResult.content
              : 'I could not complete the workout request. Please try again with a broader workout request.',
            placeholderIndex
          )
          break
        }
        toolLoopCount++
        if (toolLoopCount > this.maxToolLoops) {
          this.finalizePlainTextResponse('I need to stop this tool sequence here. Please try again with a more specific workout request.', placeholderIndex)
          break
        }

        const shouldStop = await this.processToolCalls(streamResult, placeholderIndex, history, requestId)
        if (!this.isRequestActive(requestId)) return ''
        if (shouldStop) break

        placeholderIndex = this.messages.push({ role: 'assistant', content: '' } as ChatMessage) - 1
        this.$emit('message-added')
      }
    }

    if (internal && userIndex >= 0) {
      this.messages.splice(userIndex, 1)
    }

    if (this.exercisesAddedInCurrentResponse && !this.exercisesAddedToWorkout) {
      this.exercisesAddedToWorkout = true
    }

    this.$emit('conversation-updated')
    const finalMessage = this.messages[placeholderIndex]
    return finalMessage && finalMessage.content ? finalMessage.content : ''
  }

  private async getAIStreamResponse (aiChatBot: AiChatBot, history: ChatMessage[], placeholderIndex: number, toolChoice: AIToolChoice, requestId: number) {
    if (this.loadingCallbacks && this.loadingCallbacks.onLoadingStarted) {
      this.loadingCallbacks.onLoadingStarted()
    }

    console.log('=== SENDING TO AI ===')
    console.log('History being sent to AI:', JSON.stringify(history.filter(msg => msg).map(msg => ({
      role: msg.role,
      content: this.shouldLogDetailedAI()
        ? msg.content ? msg.content.substring(0, 100) + '...' : ''
        : this.getLogContent(msg.content),
      tool_calls: msg.tool_calls ? this.getLogToolCalls(msg.tool_calls) : undefined
    })), null, 2))
    console.log('Available tools:', this.tools.map(t => t.function.name))

    const streamResponse = await aiChatBot.answerConversationStreamWithUsage(history, (delta: string) => {
      if (!this.isRequestActive(requestId)) return
      this.messages[placeholderIndex].content += delta
      this.$emit('message-delta', { index: placeholderIndex, delta })
    }, toolChoice, false)

    if (!this.isRequestActive(requestId)) return null

    if (this.loadingCallbacks && this.loadingCallbacks.onLoadingStopped) {
      this.loadingCallbacks.onLoadingStopped()
    }

    if (streamResponse.usage) {
      console.log(`AI Call Token Usage:`)
      console.log('   Usage object:', streamResponse.usage)

      if (streamResponse.usage.prompt_tokens !== undefined) {
        console.log(`   Input tokens: ${streamResponse.usage.prompt_tokens}`)
      }
      if (streamResponse.usage.completion_tokens !== undefined) {
        console.log(`   Output tokens: ${streamResponse.usage.completion_tokens}`)
      }
      if (streamResponse.usage.total_tokens !== undefined) {
        console.log(`   Total tokens: ${streamResponse.usage.total_tokens}`)
        console.log(`   Session total: ${this.totalTokensUsed + streamResponse.usage.total_tokens}`)
      }
    }

    if (this.checkTokenLimit(streamResponse.usage)) {
      return null
    }

    return streamResponse.result
  }

  private async processToolCalls (streamResult: any, placeholderIndex: number, history: ChatMessage[], requestId: number = this.activeRequestId): Promise<boolean> {
    const toolCalls = this.sanitizeToolCalls(streamResult.tool_calls || [])
    this.messages[placeholderIndex].content = null
    this.messages[placeholderIndex].tool_calls = toolCalls

    const toolResponses: ChatMessage[] = []
    let shouldStop = false

    try {
      if (this.loadingCallbacks && this.loadingCallbacks.onToolLoadingStarted) {
        this.loadingCallbacks.onToolLoadingStarted()
      }

      for (const toolCall of toolCalls) {
        if (!this.isRequestActive(requestId)) return true
        console.log('tool call', this.shouldLogDetailedAI()
          ? toolCall
          : { id: toolCall.id, function: { name: toolCall.function && toolCall.function.name, arguments: '[redacted]' } })

        // Set specific loading message based on tool function
        const toolName = toolCall.function && toolCall.function.name || ''
        const loadingMessage = this.getLoadingMessageForTool(toolName)
        if (this.loadingCallbacks && this.loadingCallbacks.setLoadingMessage) {
          this.loadingCallbacks.setLoadingMessage(loadingMessage)
        }

        this.currentToolRequestId = requestId
        const result = await this.executeToolCall(toolCall)
        if (!this.isRequestActive(requestId)) return true
        const shouldRetry = this.shouldRetryAfterToolResult(toolName, result)
        if (shouldRetry) {
          this.addRetryInstruction(history, 'The build could not meet the requested duration because the filtered pool was exhausted. First broaden one relaxable optional filter, call apply_filter_tags or undo_tags, then call build_workout_from_filtered again. If no relaxable optional filter remains and the planner reports repeat_fill_possible, call ask_question with clickable options to broaden filters or allow repeated exercises. If the trainer allows repeats, call build_workout_from_filtered again with allow_repeated_exercises true.')
        }
        if (this.isPlainTextAllowedAfterTool(toolName) && !shouldRetry) {
          this.allowPlainTextResponse = true
        }
        toolResponses.push({
          role: ConversationRoles.TOOL,
          content: result,
          tool_call_id: toolCall.id
        })
        if (toolName === 'close_chat') {
          shouldStop = true
          break
        }
      }
    } finally {
      this.currentToolRequestId = 0
      if (this.loadingCallbacks && this.loadingCallbacks.onToolLoadingStopped) {
        this.loadingCallbacks.onToolLoadingStopped()
      }
    }

    if (!this.isRequestActive(requestId) && !shouldStop) return true
    this.messages.push(...toolResponses)
    history.push(this.messages[placeholderIndex], ...toolResponses)

    return shouldStop
  }

  private sanitizeToolCalls (toolCalls: any[]): any[] {
    const normalizedToolCalls = toolCalls.map(toolCall => this.normalizeMalformedAskQuestionToolCall(toolCall))
    const askQuestionCalls = normalizedToolCalls.filter(toolCall => toolCall.function && toolCall.function.name === 'ask_question')
    if (askQuestionCalls.length) {
      return [askQuestionCalls[0]]
    }
    return normalizedToolCalls
  }

  private normalizeMalformedAskQuestionToolCall (toolCall: any): any {
    if (!toolCall || !toolCall.function || toolCall.function.name === 'ask_question') return toolCall

    const parsed = this.parseToolArguments(toolCall.function.arguments)
    if (!parsed) return toolCall

    const askQuestionArgs = this.extractAskQuestionArguments(parsed)
    if (!askQuestionArgs) return toolCall

    return {
      ...toolCall,
      type: toolCall.type || 'function',
      function: {
        ...toolCall.function,
        name: 'ask_question',
        arguments: JSON.stringify(askQuestionArgs)
      }
    }
  }

  private parseToolArguments (argumentsText: any): any | null {
    if (typeof argumentsText !== 'string' || !argumentsText.trim().length) return null
    try {
      return JSON.parse(argumentsText)
    } catch (e) {
      return null
    }
  }

  private extractAskQuestionArguments (parsed: any): any | null {
    if (!parsed || typeof parsed !== 'object') return null

    if (parsed.name === 'ask_question' && parsed.parameters && typeof parsed.parameters === 'object') {
      return this.hasQuestionAndOptions(parsed.parameters) ? parsed.parameters : null
    }

    // the check above already handles every value this one accepts
    /* istanbul ignore if */
    if (parsed.type === 'function' && parsed.name === 'ask_question' && parsed.parameters && typeof parsed.parameters === 'object') {
      return this.hasQuestionAndOptions(parsed.parameters) ? parsed.parameters : null
    }

    return this.hasQuestionAndOptions(parsed) ? parsed : null
  }

  private hasQuestionAndOptions (value: any): boolean {
    return !!value &&
      typeof value.question === 'string' &&
      Array.isArray(value.options)
  }

  private normalizeQuestionOptionsForSlot (slot: WorkoutRequestSlot | null, options: string[]): string[] {
    const cleanedOptions = options
      .map(option => option.trim())
      .filter(option => !!option)

    if (slot === 'rangeOfMotion') {
      return this.normalizeRangeOfMotionQuestionOptions(cleanedOptions)
    }

    const allowedOptions = this.getAllowedQuestionOptionsForSlot(slot)
    if (!allowedOptions.length) return cleanedOptions

    const normalizedAllowed = allowedOptions.map(option => this.normalizeIntakeValue(option))
    return cleanedOptions.filter(option => normalizedAllowed.indexOf(this.normalizeIntakeValue(option)) !== -1)
  }

  private normalizeRangeOfMotionQuestionOptions (options: string[]): string[] {
    const knownOptions = this.getKnownTagValues('ExerciseTags::RangeOfMotion', ['full', '50', '25'])
    const fullOption = knownOptions.find(option => this.normalizeRangeOfMotionValue(option) === 'full') || 'full'
    const uniqueOptions = [fullOption].concat(options).reduce((acc, option) => {
      const normalizedOption = this.normalizeRangeOfMotionValue(option)
      if (!normalizedOption || acc.seen.indexOf(normalizedOption) !== -1) return acc

      acc.seen.push(normalizedOption)
      acc.options.push(option)
      return acc
    }, { seen: [], options: [] } as { seen: string[], options: string[] }).options

    return uniqueOptions.sort((a, b) => {
      const orderDiff = this.getRangeOfMotionOrder(a) - this.getRangeOfMotionOrder(b)
      return orderDiff || a.localeCompare(b)
    })
  }

  private normalizeRangeOfMotionValue (range: string): string {
    const normalized = (range || '').toString().trim().toLowerCase()
    if (normalized === '50' || normalized === '50%') return '50%'
    if (normalized === '25' || normalized === '25%') return '25%'
    return normalized
  }

  private getAllowedQuestionOptionsForSlot (slot: WorkoutRequestSlot | null): string[] {
    if (slot === 'category') return this.getKnownTagValues('ExerciseTags::Category', DEFAULT_CATEGORIES)
    if (slot === 'execution') return this.getKnownTagValues('ExerciseTags::Execution', DEFAULT_EXECUTIONS)
    return []
  }

  private shouldRetryAfterToolResult (toolName: string, result: string): boolean {
    if (toolName !== 'build_workout_from_filtered') return false
    try {
      const parsed = JSON.parse(result)
      return parsed &&
        parsed.success === false &&
        parsed.planner &&
        parsed.planner.failure_reason === 'filtered_pool_exhausted'
    } catch (e) {
      return false
    }
  }

  private async executeToolCall (toolCall: any): Promise<string> {
    const name = toolCall.function && toolCall.function.name
    let result = ''

    if (!name) {
      result = 'Error: Invalid tool call - missing function name'
    } else if (!this.toolHandlers[name]) {
      result = `Error: No handler found for tool: ${name}`
    } else {
      let argsObj
      try {
        argsObj = toolCall.function.arguments ? JSON.parse(toolCall.function.arguments) : {}
      } catch (e) {
        result = `Error: Invalid arguments - ${e.message}`
      }
      if (argsObj) {
        try {
          result = await this.toolHandlers[name](argsObj)
        } catch (e) {
          result = `Error: Execution failed - ${e.message}`
        }
      }
    }

    console.log('tool calls', this.shouldLogDetailedAI() ? result : '[redacted]')
    return result
  }

  private handleAIResponseError (error: any): void {
    console.error('AI response failed:', error)
    if (this.loadingCallbacks && this.loadingCallbacks.onLoadingStopped) {
      this.loadingCallbacks.onLoadingStopped()
    }
    if (this.loadingCallbacks && this.loadingCallbacks.onToolLoadingStopped) {
      this.loadingCallbacks.onToolLoadingStopped()
    }
    this.messages.push({
      role: ConversationRoles.ASSISTANT,
      content: 'The AI request failed. You can retry the request or send a new one.'
    } as ChatMessage)
    this.$emit('conversation-updated')
  }

  private checkTokenLimit (usage: any): boolean {
    if (usage && usage.total_tokens) {
      this.totalTokensUsed += usage.total_tokens
      console.log(`Tokens used this request: ${usage.total_tokens}, Total: ${this.totalTokensUsed}`)

      if (this.totalTokensUsed >= this.tokenThreshold) {
        this.conversationStopped = true
        this.messages.push({
          role: ConversationRoles.ASSISTANT,
          content: 'I\'ve reached the maximum token limit for this conversation. To continue helping you with your workout planning, please start a new chat session. Thank you for understanding!'
        } as ChatMessage)
        this.$emit('conversation-updated')
        return true
      }
    }
    return false
  }

  private finalizePlainTextResponse (result: string, placeholderIndex: number): void {
    this.messages[placeholderIndex].content = result
    if (result.trim() === '') {
      this.messages.splice(placeholderIndex, 1)
    }
  }

  // Exercise & Workout Utilities
  private getExerciseDuration (exercise: ExerciseResponse): number {
    const tempWorkoutExercise = new WorkoutExercise()
    tempWorkoutExercise.setExercise(exercise)
    if (this.club) {
      return tempWorkoutExercise.getExerciseDuration(this.club, this.workout.type, this.workout.circleWorkout)
    }
    return exercise.durationMedium || 45
  }

  private computeWorkoutDurationSeconds (workout: WorkoutData = this.workout): number {
    return this.selectedWorkoutExercises.reduce((sum, workoutExercise) => {
      if (this.club) {
        return sum + workoutExercise.getExerciseDuration(this.club, workout.type, workout.circleWorkout)
      }
      return sum + (workoutExercise.repetitionDuration || workoutExercise.exercise.durationMedium || 45)
    }, 0)
  }

  private getCurrentWorkoutSummary (includeExercises: boolean = true): any {
    const totalDurationSeconds = this.computeWorkoutDurationSeconds()
    const requiredEquipment = Array.from(new Set(
      this.selectedWorkoutExercises.reduce((equipment: string[], workoutExercise) => {
        return equipment.concat(workoutExercise.exercise.equipment || [])
      }, [])
    )).sort()
    const summary = {
      workout_name: this.workout.name || null,
      total_duration_minutes: Math.ceil(totalDurationSeconds / 60),
      total_duration_seconds: totalDurationSeconds,
      total_exercise_count: this.selectedWorkoutExercises.length,
      required_equipment: requiredEquipment
    }

    if (!includeExercises) return summary

    return {
      ...summary,
      exercises: this.getCurrentWorkoutExerciseDetails()
    }
  }

  private getCompactCurrentWorkoutSummary (includeExercises: boolean = false): any {
    return this.getCurrentWorkoutSummary(includeExercises)
  }

  private getCurrentWorkoutExerciseDetails (): any[] {
    return this.selectedWorkoutExercises.map((workoutExercise, index) => {
      const durationSeconds = this.club
        ? workoutExercise.getExerciseDuration(this.club, this.workout.type, this.workout.circleWorkout)
        : workoutExercise.repetitionDuration || workoutExercise.exercise.durationMedium || 45
      const equipment = Array.isArray(workoutExercise.exercise.equipment) ? workoutExercise.exercise.equipment : []
      return {
        position: index,
        name: `${this.$t('EXERCISE.' + workoutExercise.exercise.number + '.NAME')}${workoutExercise.exercise.side ? ` (${workoutExercise.exercise.side})` : ''}`,
        duration_seconds: durationSeconds,
        duration_minutes: Math.round(durationSeconds / 60 * 100) / 100,
        equipment,
        side: workoutExercise.exercise.side || null
      }
    })
  }

  private getPlannerToolSummary (plannerResult: any): any {
    return {
      success: plannerResult.success,
      target_duration_seconds: plannerResult.target_duration_seconds,
      actual_duration_seconds: plannerResult.actual_duration_seconds,
      duration_shortfall_seconds: plannerResult.duration_shortfall_seconds,
      tolerance_seconds: plannerResult.tolerance_seconds,
      total_exercise_count: plannerResult.total_exercise_count,
      unique_exercise_count: plannerResult.unique_exercise_count,
      available_unique_exercise_count: plannerResult.available_unique_exercise_count,
      repeat_fill_possible: plannerResult.repeat_fill_possible,
      excluded_for_health: plannerResult.excluded_for_health,
      failure_reason: plannerResult.failure_reason,
      error: plannerResult.error,
      warnings: plannerResult.warnings
    }
  }

  private mapToolArgsToExerciseTags (args: any): any {
    const mapped = {}
    Object.keys(args || {}).forEach(key => {
      if (key === 'desired_duration_minutes') return
      const tagType = this.toolArgToExerciseTagType(key)
      if (!tagType) return
      const value = args[key]
      const values = Array.isArray(value) ? value : value ? [value] : []
      if (values.length) {
        mapped[tagType] = values
      }
    })
    return mapped
  }

  private toolArgToExerciseTagType (key: string): string | null {
    const mapping = {
      body_area: 'ExerciseTags::BodyArea',
      movement_pattern: 'ExerciseTags::MovementPattern',
      focus: 'ExerciseTags::Focus',
      training_focus: 'ExerciseTags::Focus',
      muscle_group: 'ExerciseTags::MuscleGroup',
      health_limitation: 'ExerciseTags::HealthLimitation',
      equipment: 'ExerciseTags::Equipment',
      category: 'ExerciseTags::Category',
      execution: 'ExerciseTags::Execution',
      exercise: 'ExerciseTags::Exercise',
      joints: 'ExerciseTags::Joints',
      range: 'ExerciseTags::RangeOfMotion',
      range_of_motion: 'ExerciseTags::RangeOfMotion',
      clinical: 'ExerciseTags::ClinicalPictures',
      clinical_pictures: 'ExerciseTags::ClinicalPictures'
    }

    return mapping[key] || null
  }

  private createExerciseListResponse (currentlyFilteredExercisesNumber: any): string {
    const count = Number(currentlyFilteredExercisesNumber)
    const filteredFacetCounts = this.getCompactLibraryFacetCounts(this.availableExercises)
    const rangeOfMotionAmbiguity = this.getRangeOfMotionAmbiguity(this.availableExercises)
    const targetDurationSeconds = this.userRequestedDurationMinutes * 60
    const poolCapacitySeconds = this.availableExercises.reduce((sum, exercise) => {
      const entryCount = exercise.hasSides ? 2 : 1
      return sum + (this.getExerciseDuration(exercise) * entryCount)
    }, 0)
    const averageEntryDurationSeconds = count
      ? Math.round(this.availableExercises.reduce((sum, exercise) => sum + this.getExerciseDuration(exercise), 0) / count)
      : 0

    if (!this.hasNoRangeOfMotionPreference &&
        rangeOfMotionAmbiguity &&
        rangeOfMotionAmbiguity.signature !== this.allowedRangeOfMotionAmbiguitySignature) {
      return this.createRangeOfMotionAmbiguityResponse(rangeOfMotionAmbiguity, {
        count,
        facet_counts: filteredFacetCounts,
        target_duration_minutes: this.userRequestedDurationMinutes,
        target_duration_seconds: targetDurationSeconds,
        pool_capacity_seconds: poolCapacitySeconds,
        average_entry_duration_seconds: averageEntryDurationSeconds
      })
    }

    return JSON.stringify({
      success: true,
      count,
      facet_counts: filteredFacetCounts,
      target_duration_minutes: this.userRequestedDurationMinutes,
      target_duration_seconds: targetDurationSeconds,
      pool_capacity_seconds: poolCapacitySeconds,
      average_entry_duration_seconds: averageEntryDurationSeconds,
      message: count === 0
        ? 'Found 0 exercises. Broaden one optional filter before building.'
        : `Filtered pool contains ${count} unique exercises. Use build_workout_from_filtered; application code will select the exercises.`
    })
  }

  private getRangeOfMotionAmbiguity (exercises: ExerciseResponse[]): RangeOfMotionAmbiguity | null {
    const groups = new Map<string, { name: string, ranges: Set<string> }>()

    ;(exercises || []).forEach(exercise => {
      const ranges = (exercise.exerciseTags || [])
        .filter(tag => tag.type === 'ExerciseTags::RangeOfMotion')
        .map(tag => tag.key.trim().toLowerCase())
        .filter(range => !!range)
      if (!ranges.length) return

      const displayName = this.getExerciseDisplayName(exercise)
      const baseName = this.normalizeExerciseBaseName(displayName)
      if (!baseName) return

      const group = groups.get(baseName) || { name: this.stripRangeOfMotionSuffix(displayName), ranges: new Set<string>() }
      ranges.forEach(range => group.ranges.add(range))
      groups.set(baseName, group)
    })

    const ambiguousGroups = Array.from(groups.values())
      .filter(group => group.ranges.size > 1)
      .sort((a, b) => a.name.localeCompare(b.name))
    if (!ambiguousGroups.length) return null

    const options = Array.from(ambiguousGroups.reduce((ranges, group) => {
      group.ranges.forEach(range => ranges.add(range))
      return ranges
    }, new Set<string>()))
      .sort((a, b) => this.getRangeOfMotionOrder(a) - this.getRangeOfMotionOrder(b) || a.localeCompare(b))
    const exerciseNames = ambiguousGroups.map(group => group.name)

    return {
      signature: `${exerciseNames.join('|')}::${options.join('|')}`,
      exerciseNames,
      options
    }
  }

  private hasUnresolvedRangeOfMotionAmbiguity (): boolean {
    if (this.hasNoRangeOfMotionPreference) return false
    const ambiguity = this.getRangeOfMotionAmbiguity(this.availableExercises)
    return !!ambiguity && ambiguity.signature !== this.allowedRangeOfMotionAmbiguitySignature
  }

  private createRangeOfMotionAmbiguityResponse (ambiguity: RangeOfMotionAmbiguity, extra: any = {}): string {
    const visibleExerciseNames = ambiguity.exerciseNames.slice(0, MAX_AMBIGUOUS_EXERCISE_NAMES)
    return JSON.stringify({
      success: false,
      requires_range_of_motion_selection: true,
      ambiguous_exercises: visibleExerciseNames,
      ambiguous_exercise_count: ambiguity.exerciseNames.length,
      ambiguous_exercises_truncated: ambiguity.exerciseNames.length > visibleExerciseNames.length,
      range_of_motion_options: ambiguity.options,
      ...extra,
      message: 'Exercises with the same base name have different ranges of motion. Call ask_question with required_range_of_motion set to true and use these exact options before building.'
    })
  }

  private getExerciseDisplayName (exercise: ExerciseResponse): string {
    const translationKey = `EXERCISE.${exercise.number}.NAME`
    return this.$te(translationKey) ? `${this.$t(translationKey)}` : exercise.getName()
  }

  private normalizeExerciseBaseName (name: string): string {
    return this.stripRangeOfMotionSuffix(name)
      .toLowerCase()
      .replace(/[\s\-_()[\]{}:;,.\/]+/g, ' ')
      .trim()
  }

  private stripRangeOfMotionSuffix (name: string): string {
    return (name || '')
      .replace(/\s*(?:[-–—:]\s*)?\(?(?:full(?:\s+range(?:\s+of\s+motion)?)?|50\s*%|25\s*%)\)?\s*$/i, '')
      .trim()
  }

  private getRangeOfMotionOrder (range: string): number {
    const order = ['full', '50%', '25%']
    const index = order.indexOf(this.normalizeRangeOfMotionValue(range))
    return index === -1 ? order.length : index
  }

  private getCompactLibraryFacetCounts (exercises: ExerciseResponse[]): ExerciseFacetCounts {
    return compactExerciseFacetCounts(
      buildExerciseFacetCounts(
        Array.isArray(exercises) ? exercises : [],
        this.exerciseTags || this.availableTags || {},
        this.getEquipmentFilterList(exercises),
        AI_LIBRARY_TAG_TYPES
      )
    )
  }

  private getEquipmentFilterList (exercises: ExerciseResponse[]): string[] {
    let equipmentList: string[] = []
    const exerciseList = exercises || []
    exerciseList.forEach(exercise => {
      const equipment = Array.isArray(exercise.equipment) ? exercise.equipment : []
      equipmentList = Array.from(new Set(equipmentList.concat(equipment)))
    })
    if (!equipmentList.length) {
      equipmentList = availableEquipment.slice()
    }
    if (!equipmentList.includes(NO_EQUIPMENT)) {
      equipmentList.unshift(NO_EQUIPMENT)
    }
    return equipmentList
  }

  private addEquipmentTags (tags: any): any {
    const currentTags = tags && typeof tags === 'object' ? tags : {}
    const aiTags = { ...currentTags }
    delete aiTags['ExerciseTags::Level']
    return { ...aiTags, 'ExerciseTags::Equipment': [ NO_EQUIPMENT, ...availableEquipment.map(equipment => equipment.toLowerCase()) ] }
  }

  private isPlainTextAllowedAfterTool (toolName: string): boolean {
    return [
      'build_workout_from_filtered',
      'remove_exercises',
      'remove_all_exercises',
      'set_workout_name',
      'check_current_workout',
      'close_chat'
    ].includes(toolName)
  }

  private isQuestionLikeResponse (response: string): boolean {
    return /[?？؟]/.test(response || '')
  }

  private convertExerciseTag (tag: string): string | null {
    const mapping = {
      'ExerciseTags::MovementPattern': 'movement',
      'ExerciseTags::Focus': 'focus',
      'ExerciseTags::MuscleGroup': 'muscle',
      'ExerciseTags::BodyArea': 'body',
      'ExerciseTags::Equipment': 'equipment',
      'ExerciseTags::HealthLimitation': 'health',
      'ExerciseTags::Category': 'category',
      'ExerciseTags::Execution': 'execution',
      'ExerciseTags::Exercise': 'exercise',
      'ExerciseTags::Joints': 'joints',
      'ExerciseTags::RangeOfMotion': 'range',
      'ExerciseTags::ClinicalPictures': 'clinical'
    }

    return mapping[tag] || null
  }

  private parseJSONTags (parsed: any): void {
    Object.entries(parsed).forEach(([key, value]: [any, any[]]) => {
      const filterType = this.convertExerciseTag(key)
      if (!filterType || !Array.isArray(value)) return
      value.forEach((item: string) => {
        this.filterFunction(item, filterType)
      })
    })
  }

  private removeJSONTags (parsed: any): boolean {
    let removedAny = false
    Object.entries(parsed).forEach(([key, value]: [any, any[]]) => {
      const filterType = this.convertExerciseTag(key)
      if (!filterType || !Array.isArray(value)) return
      value.forEach((item: string) => {
        if (this.removeFilterFunction) {
          const selectedFilters = this.$store.state.exerciseFilters && this.$store.state.exerciseFilters.tags
          const selectedFilterValues = selectedFilters && selectedFilters[filterType]
          if (selectedFilterValues && selectedFilterValues.has && selectedFilterValues.has(item)) {
            removedAny = true
          }
          this.removeFilterFunction(item, filterType)
        }
      })
    })
    return removedAny
  }

  private waitForFilterEvent (type: string): Promise<any> {
    return new Promise(resolve => {
      const timeout = setTimeout(() => {
        eventSystem.removeEventListener(type, this.filterEventHandler)
        resolve(this.availableExercises.length)
      }, 2000)
      this.filterEventHandler = (e: CustomEvent) => {
        clearTimeout(timeout)
        eventSystem.removeEventListener(type, this.filterEventHandler)
        resolve(e.detail.message)
      }
      eventSystem.addEventListener(type, this.filterEventHandler)
    })
  }

  private removeFilterByName (value: string) {
    this.$store.dispatch('filterExercises', { name: null })
  }
}
